import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useUserSettingsStore, useTenantsStore, useSitesStore, useUsersStore } from '@/stores';
import type IUser from "@/models/responses/IUser";
import type ITenant from "@/models/responses/ITenant";
import type ISite from "@/models/responses/ISite";
import HasRoleRequest from "@/models/requests/HasRoleRequest";
import { RoleCodes } from "@/models/RoleCodes";
import { loadTenantMasterdata } from '@/services/storeLoader';
import { ref } from "vue";
import Site from "@/models/frontend/Site";
import Tenant from "@/models/frontend/Tenant";
import User from "@/models/frontend/User";

export const useSessionStore = defineStore("session", {
  state: () => ({
    user: ref(null as User | null),
    userTenants: ref([] as Tenant[]),
    userSites: ref([] as Site[]),
    selectedTenantId: ref(0),
    selectedSiteId: ref(0),
    isSystemAdmin: ref(false),
    isTenantAdmin: ref(false),
    isSiteAdmin: ref(false),
    error: "" as any,
    loadedUser: false,
    loadedTenantsForUserId: 0,
    loadedSitesForUserId: 0,
    loadedHasSysAdminForUserId: 0,
    loadedIsTenantAdminForUserAndTenant: { userId: 0, tenantId: 0 },
    loadedIsSiteAdminForUserAndSite: { userId: 0, siteId: 0 },
  }),
  actions: {
    resetAll() {
      this.user = null;
      this.userTenants = [];
      this.userSites = [];
      this.selectedTenantId = 0;
      this.selectedSiteId = 0;
      this.isSystemAdmin = false;
      this.isTenantAdmin = false;
      this.isSiteAdmin = false;
      this.error = "";
      this.loadedUser = false;
      this.loadedTenantsForUserId = 0;
      this.loadedSitesForUserId = 0;
      this.loadedHasSysAdminForUserId = 0;
    },
    async fetchAll(force = false) {
      await this.loadUser(force);
      await Promise.all([
        this.loadUserTenants(force),
        this.loadUserSites(force),
        this.loadHasSysAdmin(force)
      ]);
    },
    async loadUser(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;

      if (!force && this.loadedUser) return;

      this.user = null;
      await api
        .get<IUser>("v1.0/users/.self")
        .then((response) => {
          const usersStore = useUsersStore();
          this.user = usersStore.mapToFrontend(response.data);
          this.loadedUser = true;
        })
        .catch((error) => (this.error = error));
    },
    async loadUserTenants(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;

      if (!this.user) return;
      const userId = this.user.id;

      if (!force && this.loadedTenantsForUserId === userId) return;
      const tenantId = this.selectedTenantId;

      this.userTenants = [];
      this.error = "";
      await api
        .get<ITenant[]>(`v1.0/users/${userId}/tenants`)
        .then((response) => {
          const tenantsStore = useTenantsStore();
          this.userTenants = response.data.map(tenantsStore.mapToFrontend);
          if (this.userTenants.findIndex((t) => t.id === tenantId) === -1) {
            this.selectedTenantId = 0;
          }
          this.loadedTenantsForUserId = userId;
        })
        .catch((error) => {
          this.selectedTenantId = 0;
          this.error = error;
        });
    },
    async loadUserSites(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;

      if (!this.user) return;
      const userId = this.user.id;

      if (!force && this.loadedSitesForUserId === userId) return;

      this.error = "";
      await api
        .get<ISite[]>(`v1.0/users/${userId}/sites`)
        .then((response) => {
          const sitesStore = useSitesStore();
          this.userSites = response.data.map(sitesStore.mapToFrontend);
          this.loadedSitesForUserId = userId;
        })
        .catch((error) => (this.error = error));
    },
    async loadHasSysAdmin(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;

      if (!this.user) return;
      const userId = this.user.id;

      if (!force && this.loadedHasSysAdminForUserId === userId) return; // assume that the authenticated user is the same as the user we are loading

      this.error = "";

      const body = new HasRoleRequest(RoleCodes.SystemAdmin, undefined, undefined, undefined, undefined);
      await api
        .post<boolean>(`v1.0/users/.self/has-role`, body)
        .then((response) => {
          this.isSystemAdmin = response.data;
          this.loadedHasSysAdminForUserId = userId;
        })
        .catch((error) => (this.error = error));

      console.debug("IsSysAdmin: " + this.isSystemAdmin);
    },
    async loadIsTenantAdmin(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;

      if (!this.user) return;
      const userId = this.user.id;
      const tenantId = this.selectedTenantId;

      if (!force && this.loadedIsTenantAdminForUserAndTenant.userId === userId && this.loadedIsTenantAdminForUserAndTenant.tenantId === tenantId) return;

      this.error = "";

      const body = new HasRoleRequest(RoleCodes.TenantAdmin, undefined, tenantId, undefined, undefined);
      await api
        .post<boolean>(`v1.0/users/.self/has-role`, body)
        .then((response) => {
          this.isTenantAdmin = response.data;
          this.loadedIsTenantAdminForUserAndTenant = { userId, tenantId: tenantId };
        })
        .catch((error) => (this.error = error));
    },
    async loadIsSiteAdmin(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;

      if (!this.user) return;
      const userId = this.user.id;
      const siteId = this.selectedSiteId;

      if (!force && this.loadedIsSiteAdminForUserAndSite.userId === userId && this.loadedIsSiteAdminForUserAndSite.siteId === siteId) return;

      this.error = "";

      const body = new HasRoleRequest(RoleCodes.SiteAdmin, undefined, undefined, siteId, true);
      await api
        .post<boolean>(`v1.0/users/.self/has-role`, body)
        .then((response) => {
          this.isSiteAdmin = response.data;
          this.loadedIsSiteAdminForUserAndSite = { userId, siteId: siteId };
        })
        .catch((error) => (this.error = error));
    },
    async selectTenant(id: number, unselectSite: boolean = true) {
      const isTenantChanged = this.selectedTenantId !== id;
      this.selectedTenantId = id;
      await this.loadIsTenantAdmin();
      console.debug("Selected tenant: " + id + " | IsTenantAdmin: " + this.isTenantAdmin);
      if (unselectSite) {
        await this.selectSite(0); // reset site selection
      }
      else if (isTenantChanged && id > 0) {
        await loadTenantMasterdata();
      }
    },
    async selectSite(id: number) {
      const isSiteChanged = this.selectedSiteId != id;
      this.selectedSiteId = id;
      await this.loadIsSiteAdmin();
      console.debug("Selected site: " + id + " | IsSiteAdmin: " + this.isSiteAdmin);
      const currentSite = this.getSelectedSite();
      if (currentSite) {
        if (this.selectedTenantId !== currentSite.tenant.id) {
          await this.selectTenant(currentSite.tenant.id, false);
        }
        const userSettingsStore = useUserSettingsStore();
        if (userSettingsStore.data.lastUsedSiteId !== currentSite.id) {
          userSettingsStore.data.lastUsedSiteId = currentSite.id;
          userSettingsStore.postUserSettings(); // fire and forget
        }
        if (isSiteChanged && id > 0) {
          await loadTenantMasterdata();
        }
      }
    },
    getSelectedTenant() {
      return this.userTenants.find((t) => t.id === this.selectedTenantId);
    },
    getSelectedSite() {
      return this.userSites.find((s) => s.id === this.selectedSiteId);
    },
  },
  getters: {
    selectedTenant: (state) => state.userTenants.find((t) => t.id === state.selectedTenantId),
    selectedSite: (state) => state.userSites.find((s) => s.id === state.selectedSiteId),
  },
});
